import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { SuggestInput } from "../components/SuggestInput";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface View {
  photo: string;
  focus: { x: number; y: number };
  zoom: number;
  guesses: string[];
  maxGuesses: number;
  /** Hint ladder per miss: years → occupation → country → first letter. */
  years?: string;
  occ?: string;
  cty?: string;
  letter?: string;
  answer?: { uk: string; years: string; occ?: string };
  credit?: string;
}

const COPY = {
  unknown_person: "Не знаю такої людини — вибери з підказок",
  already_guessed: "Вже було",
};

export function Foto({ date }: { date?: string | undefined }) {
  const g = useGame<View>("foto", COPY, date);
  const [names, setNames] = useState<{ uk: string; en: string }[]>([]);

  useEffect(() => {
    void api.names("foto").then((r) => setNames(r.names)).catch(() => {});
  }, []);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const left = v.maxGuesses - v.guesses.length;
  const wrongs = won ? v.guesses.slice(0, -1) : v.guesses;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ХТО НА ФОТО?" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mx-auto aspect-square w-full max-w-[340px] overflow-hidden rounded-2xl bg-tgsection">
          <img
            src={v.photo}
            alt="хто це?"
            className="h-full w-full object-cover"
            style={{
              transform: `scale(${v.zoom})`,
              transformOrigin: `${v.focus.x}% ${v.focus.y}%`,
              transition: "transform .5s ease-out",
            }}
            referrerPolicy="no-referrer"
          />
        </div>
        <div className="mt-2 text-center text-xs text-tghint">
          {finished ? "фото з Wikimedia Commons" : `хто це? спроб лишилось: ${left}`}
        </div>
        {!finished && (v.years || v.occ || v.cty || v.letter) && (
          <div className="mt-2 flex flex-wrap justify-center gap-1.5">
            {v.years && <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">💡 {v.years}</span>}
            {v.occ && <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">💼 {v.occ}</span>}
            {v.cty && <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">🌍 {v.cty}</span>}
            {v.letter && <span className="rounded-full bg-warm/20 px-2.5 py-1 text-[11px] font-medium">💡 на «{v.letter}»</span>}
          </div>
        )}

        {wrongs.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {[...wrongs].reverse().map((t) => (
              <li key={t} className="rounded-xl bg-tgsection px-3 py-2 text-sm">
                ❌ {t}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
        <SuggestInput
          names={names}
          disabled={finished}
          busy={g.busy}
          exclude={guessed}
          onSubmit={(name) => void g.run(() => api.guess<View>("foto", name, date))}
          placeholder="ім'я або прізвище"
        />
        {!finished && v.guesses.length >= 2 && (
          <div className="mt-2 flex px-4">
            <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("foto", date))} />
          </div>
        )}
      </div>

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Впізнав!" : "Не сьогодні"}
          subtitle={won ? `за ${v.guesses.length} · стрік ${s.me.streak} 🔥` : "стрік згорів"}
          scoreLabel="avg спроб"
          formatScore={(n) => `${n}`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.answer && (
            <div className="mt-4 rounded-xl bg-[rgba(128,128,128,0.12)] px-4 py-3 text-center">
              <div className="text-lg font-bold">{v.answer.uk}</div>
              <div className="mt-0.5 text-xs text-tghint">
                {v.answer.years}
                {v.answer.occ ? ` · ${v.answer.occ}` : ""}
              </div>
              {v.credit && (
                <a href={v.credit} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-tgbutton underline">
                  фото на Commons ↗
                </a>
              )}
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
